import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getThreadDraft, setThreadDraft } from "@/contexts/chat-draft-store";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/utils/custom-types";
import { queryClient, trpc } from "@/utils/trpc";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { FileIcon, Play, Plus, Send, Smile, Volume2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { handleDropInput, handlePasteAttachments, linkifyText, mimeFromExt, type PendingAttachment } from "./chat-helpers";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { useChannelLinkHandler } from "@/hooks/use-channel-link-handler";

export function ChatThread({
	channelId,
    workspaceId,
    session,
    activeThreadId,
    activeThreadMessage,
    onClose,
    onMediaClick,
}: {
	channelId: number;
    workspaceId: number;
	session?: any;
    activeThreadId: number;
    activeThreadMessage: any;
    onClose: () => void;
	onMediaClick?: (src: string, alt: string, kind: "image" | "video") => void;
}) {
	const { handleLinkClick } = useChannelLinkHandler();
	const draftKey = String(activeThreadId)!;
	const existing = getThreadDraft(draftKey);

	const [threadMessageText, setThreadMessageText] = useState(existing?.text ?? "");
	const [isDraggingThreadFile, setIsDraggingThreadFile] = useState(false);
	const [pendingThreadAttachments, setPendingThreadAttachments] = useState<PendingAttachment[]>(existing?.attachments ?? []);

	const threadScrollContainerRef = useRef<HTMLDivElement | null>(null);
	const shouldAutoScrollThreadRef = useRef(true);
	const threadFileInputRef = useRef<HTMLInputElement | null>(null);
	const threadMessageListRef = useRef<HTMLDivElement | null>(null);

	const hasSetInitialScrollRef = useRef(false);

	const saveScrollRafRef = useRef<number | null>(null);
	const rafRef = useRef<number | null>(null);
	const isLoadingOlderRef = useRef(false);

    const messagesQueryKey = trpc.message.getByChannel.queryKey({
		channelId: channelId
	});

    const threadMessagesQueryKey = trpc.message.getByThread.queryKey({
		threadId: activeThreadId
	});

    const fetchMessages = async ({ pageParam }: { pageParam?: number }) => {
		return queryClient.fetchQuery({
			...trpc.message.getByThread.queryOptions({
				channelId: Number(channelId),
                threadId: activeThreadId,
				limit: 50,
				cursor: pageParam,
			}),
			staleTime: 0
		})
	}

	const { 
		data: messagesInfinite,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		isFetching
	} = useInfiniteQuery({
		queryKey: threadMessagesQueryKey,
		queryFn: fetchMessages,
		initialPageParam: undefined as number | undefined,
		getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
  		refetchOnMount: "always",
	})

	const isSubscriptionSeeded = Boolean((messagesInfinite as any)?.__subscriptionSeeded);
	const showInitialLoading = isLoading || (isSubscriptionSeeded && isFetching && !isFetchingNextPage);

    const messagesData = useMemo(() => {
		const pages = messagesInfinite?.pages ?? [];
		const messages = pages
			.slice()
			.reverse()             
			.flatMap((p) => p.messages ?? []);

		const last = pages[pages.length - 1];

		return {
			messages,
			hasMore: last?.hasMore ?? false,
			nextCursor: last?.nextCursor,
		};
	}, [messagesInfinite])

	const scrollThreadToBottom = () => {
		const el = threadScrollContainerRef.current;
		if (!el) return;

		el.scrollTop = el.scrollHeight;
	};

    const createMessageMutation = useMutation(
		trpc.message.create.mutationOptions({
			onSuccess: (data, variables) => {
				const tempId = variables.tempId;

				queryClient.setQueryData(threadMessagesQueryKey, (prev: any) => {
					if (!prev?.pages?.length) return prev;
			
					const pages = prev.pages.map((page: any) => {
						const msgs = page.messages ?? [];
						const idx = msgs.findIndex((m: any) => m.tempId === tempId);
						if (idx === -1) return page;
		
						const next = [...msgs];
					
						next[idx] = {
							...next[idx],
							id: data.id,
							sending: false,
							threadRootId: data.threadRootId
						};

						return { ...page, messages: next };
					});
			
					return { ...prev, pages };
				});
			}
		}),
	);

	const createPresignedFileUploadMutation = useMutation(
		trpc.message.createPresignedFileUpload.mutationOptions(),
	);

    const groupedThreadMessages = useMemo(() => {
		const grouped: Array<{
			key: string;
			senderName: string;
			createdAt: string;
			messages: any[];
		}> = [];

        const all = [
            activeThreadMessage,
            ...messagesData.messages
        ].filter(Boolean);
	  
		for (const msg of all) {
			const sender = msg.senderName || "Unknown";

            if (msg.id === activeThreadMessage.id) {
                grouped.push({
                    key: `${sender}-root-thread-message-${msg.id}`,
                    senderName: sender,
                    createdAt: msg.createdAt,
                    messages: [msg],
                });

                continue;
            }

            const msgDate = new Date(msg.createdAt);

            const minuteKey = `${sender}-${msgDate.getFullYear()}-${msgDate.getMonth()}-${msgDate.getDate()}-${msgDate.getHours()}-${msgDate.getMinutes()}`;

            const prev = grouped[grouped.length - 1];

            if (prev && prev.senderName === sender && prev.key === minuteKey) {
                prev.messages.push(msg);
            } else {
                grouped.push({
                    key: minuteKey,
                    senderName: sender,
                    createdAt: msg.createdAt,
                    messages: [msg],
                });
            }
		}
	  
		return grouped;
	}, [messagesData]);



    useEffect(() => {
		const scroll = getThreadDraft(draftKey)?.scroll;

		setThreadDraft(draftKey, {
			text: threadMessageText,
			attachments: pendingThreadAttachments,
			scroll: scroll,
		});
	}, [draftKey, threadMessageText, pendingThreadAttachments]);

    useEffect(() => {
		hasSetInitialScrollRef.current = false;
	}, [draftKey]);

    useLayoutEffect(() => {
		const el = threadScrollContainerRef.current;
		if (!el) return;
		
		if (hasSetInitialScrollRef.current) return;
	  
		if ((messagesData?.messages?.length ?? 0) === 0) return;

		const savedScroll = getThreadDraft(draftKey)?.scroll;

		if (savedScroll?.mode === "bottom") {
			el.scrollTop = el.scrollHeight;
		} else if (savedScroll?.mode === "anchor") {
			const node = el.querySelector<HTMLElement>(
				`[data-message-id="${savedScroll.messageId}"]`
			);

			if (node) {
				const containerRect = el.getBoundingClientRect();
				const nodeRect = node.getBoundingClientRect();

				const nodeTopInContent = el.scrollTop + (nodeRect.top - containerRect.top);

				const targetScrollTop = nodeTopInContent - savedScroll.offsetPx;
				el.scrollTop = targetScrollTop;
			} else {
				if (typeof savedScroll.fallbackScrollTop === "number") {
				  	el.scrollTop = savedScroll.fallbackScrollTop;
				}
			}
		} else {
			el.scrollTop = el.scrollHeight;
		}
	
		hasSetInitialScrollRef.current = true;
	
		const bottomThresholdPx = 80;
		const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		shouldAutoScrollThreadRef.current = distanceFromBottom < bottomThresholdPx;
	}, [draftKey, isFetching, messagesData]);

    const loadOlderPreserveScroll = async () => {
		const el = threadScrollContainerRef.current;
		if (!el || !hasNextPage || isFetchingNextPage) return;

		if (isLoadingOlderRef.current) return;
  		isLoadingOlderRef.current = true;

		const prevScrollHeight = el.scrollHeight;
	  
		await fetchNextPage();

		requestAnimationFrame(() => {
			const el2 = threadScrollContainerRef.current;
			if (!el2) return;
			const delta = el2.scrollHeight - prevScrollHeight;
			el2.scrollTop = el2.scrollTop + delta;

			isLoadingOlderRef.current = false;
		})
	}

    useEffect(() => {
		const el = threadScrollContainerRef.current;
		if (!el) return;

		const onScroll = () => {
			const bottomThresholdPx = 80;
			const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			shouldAutoScrollThreadRef.current = distanceFromBottom < bottomThresholdPx;

			if (saveScrollRafRef.current == null) {
				saveScrollRafRef.current = requestAnimationFrame(() => {
					saveScrollRafRef.current = null;
				
					const current = getThreadDraft(draftKey) ?? { text: "", attachments: [] };

					if (shouldAutoScrollThreadRef.current) {
						setThreadDraft(draftKey, {
							...current,
							scroll: { mode: "bottom" },
						});
						return;
					}

					const containerRect = el.getBoundingClientRect();
    				const nodes = el.querySelectorAll<HTMLElement>("[data-message-id]");

					let snapshot: { mode: "anchor"; messageId: string; offsetPx: number; fallbackScrollTop: number } | undefined;

					for (let i = 0; i < nodes.length; i++) {
						const node = nodes[i]!;
						const r = node.getBoundingClientRect();

						if (r.bottom > containerRect.top + 1) {
							const messageId = node.dataset.messageId;
							if (messageId) {
								snapshot = {
									mode: "anchor",
									messageId,
									offsetPx: r.top - containerRect.top,
									fallbackScrollTop: el.scrollTop,
								};
							}
							break;
						}
					}

					// console.log("[chat-view] Saving scroll snapshot:", snapshot ?? current.scroll);
					setThreadDraft(draftKey, {
						...current,
						scroll: snapshot ?? current.scroll,
					});
				});
			}

			if (!hasNextPage || isFetchingNextPage) return;

			const maxScrollTop = el.scrollHeight - el.clientHeight;
			if (maxScrollTop <= 0) return;

			const progressFromTop = el.scrollTop / maxScrollTop;
			if (progressFromTop <= 0.3) {
				if (rafRef.current != null) return;
				if (isLoadingOlderRef.current) return;

				rafRef.current = requestAnimationFrame(async () => {
					await loadOlderPreserveScroll();
					rafRef.current = null;
				})
			}
		}

		el.addEventListener("scroll", onScroll, { passive: true });
		
		return () => el.removeEventListener("scroll", onScroll);
	}, [draftKey, hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
		const root = threadMessageListRef.current;
		if (!root) return;
	  
		const ro = new ResizeObserver(() => {
			// if (!hasSetInitialScrollRef.current) return;
			
			if (shouldAutoScrollThreadRef.current) {
				scrollThreadToBottom();
			}
		});
	  
		ro.observe(root);
		return () => ro.disconnect();
	}, []);

	const uploadPendingFiles = async () => {
        const files = pendingThreadAttachments.slice();
        setPendingThreadAttachments([]);

        if (files.length === 0) return [];

		const presignRes = await createPresignedFileUploadMutation.mutateAsync({
			channelId: Number(channelId),
			files: files.map(a => ({
				fileName: a.file.name,
				contentType: a.file.type || "application/octet-stream",
				size: a.file.size,
			}))
		});

		await Promise.all(
			presignRes.uploads.map(async (u, idx) => {
				const file = files[idx]!.file;
				const putRes = await fetch(u.uploadUrl, {
					method: "PUT",
					headers: { "Content-Type": file.type || "application/octet-stream" },
					body: file,
				});
				if (!putRes.ok) {
					toast.error(`Upload failed (${putRes.status})`);
					throw new Error(`Upload failed (${putRes.status})`);
				}
			})
		)

		return presignRes.uploads.map(u => ({
			url: u.src,
			type: u.contentType,
			name: u.originalName,
			size: u.size,
		}));
	}

	const handleSend = async (threadId: number) => {
		if (!channelId) return;

        const trimmedStart = threadMessageText.trim();
  		const validation = trimmedStart;

		const optimisticAttachments = pendingThreadAttachments.map(a => ({
            url: a.previewUrl ?? undefined,        
            type: a.file.type,
            name: a.file.name,
            size: a.file.size,
        }));
		
		if (!validation && optimisticAttachments.length === 0) return;

		const tempId = crypto.randomUUID();

		const optimisticId = -Date.now();
        const createdAt = new Date().toISOString();

        queryClient.setQueryData(messagesQueryKey, (prev: any) => {
            if (!prev?.pages) return prev;

            const pages = prev.pages.map((p: any) => ({
                ...p,
                messages: p.messages.map((m: any) =>
                    m.id === activeThreadMessage.id ? 
                        { 
                            ...m, 
                            replyCount: Number(m.replyCount ?? 0) + 1,
                            lastReplyAt: createdAt,
                            lastReplySenderId: session.user.id
                        } : m
                ),
            }));

            return { ...prev, pages };
        });

		queryClient.setQueryData(threadMessagesQueryKey, (prev: any) => {
			const optimisticMessage = {
				id: optimisticId,
				tempId,
				senderId: session.user.id,
  				sending: true,
				workspaceId: Number(workspaceId),
				channelId: Number(channelId),
				senderName: session?.user?.name || "You",
				text: trimmedStart,
				attachments: optimisticAttachments,
				createdAt: createdAt,
				threadRootId: threadId,
			};

			if (!prev?.pages.length) {
				return {
					pages: [{ messages: [optimisticMessage], hasMore: false, nextCursor: undefined }],
					pageParams: [undefined],
				}
			}

			const pages = [...prev.pages];
  			const firstPage = pages[0] ?? { messages: [], hasMore: false, nextCursor: undefined };

			pages[0] = {
				...firstPage,
				messages: [...(firstPage.messages ?? []), optimisticMessage],
			};

			return { ...prev, pages };
		});

        shouldAutoScrollThreadRef.current = true;

		let uploaded = [];
		try {
            setThreadMessageText("");
			uploaded = await uploadPendingFiles();
		} catch (e) {
			toast.error("Failed to upload file(s)");
			return;
		}

		// console.log("uploaded: ", JSON.stringify(uploaded, null, 2));

		createMessageMutation.mutate(
			{
				workspaceId: Number(workspaceId),
				channelId: Number(channelId),
				text: trimmedStart,
				attachments: uploaded,
				tempId: tempId,
				threadRootId: threadId,
			},
		);
	};

	return (
		<div className="flex h-full flex-col bg-background">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border px-4 py-2">
                <div className="font-semibold">Thread</div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    aria-label="Close thread"
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <div className="relative flex-1 overflow-y-auto overflow-x-hidden pb-4 [overflow-anchor:none]" ref={threadScrollContainerRef}>
                {showInitialLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
                    </div>
                )}

                <div ref={threadMessageListRef}>
                    {groupedThreadMessages.length !== 0 ? (
                        <div>
                            {groupedThreadMessages.map((group, idx) => {
                                let timeLabel = "";
                                if (group.createdAt) {
                                    const date = new Date(group.createdAt);
                                    const now = new Date();
                                    const isToday =
                                    date.getFullYear() === now.getFullYear() &&
                                    date.getMonth() === now.getMonth() &&
                                    date.getDate() === now.getDate();
                        
                                    timeLabel = isToday
                                    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                    : date.toLocaleDateString() +
                                        " " +
                                        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                                }
                    
                                return (
                                    <div key={group.key + "-" + idx} className="flex">
                                        <div className="flex-1">
                                            {group.messages.map((msg: any, i: number) => {
                                                const isFirst = i === 0;
                                                const isRoot = msg.id === activeThreadId;
                                                const replyCount = messagesData.messages.length;

                                                return (
                                                    <div key={msg.id}>
                                                        <div
                                                            className={cn(
                                                                "relative flex hover:bg-accent pl-2 pr-8", 
                                                                isFirst && "pt-2"
                                                            )}
                                                            data-message-id={String(msg.id)}
                                                        >
                                                            <div className="w-8 flex-shrink-0 pt-1">
                                                                {isFirst ? (
                                                                    <div className="h-8 w-8 rounded-full bg-primary/20" />
                                                                ) : (
                                                                    <div className="w-8 opacity-0" />
                                                                )}
                                                            </div>
                                        
                                                            <div className="min-w-0 flex-1 pl-3">
                                                                {isFirst && (
                                                                    <div className="flex items-baseline gap-2 pt-1">
                                                                        <span className="font-semibold text-sm">{group.senderName}</span>
                                                                        <span className="text-muted-foreground text-xs">{timeLabel}</span>
                                                                    </div>
                                                                )}
                                        
                                                                {!!msg.text && (
                                                                    <p className={cn(
                                                                            "text-foreground whitespace-pre-wrap break-words pr-2", 
                                                                            msg.sending && "opacity-60 select-none"
                                                                        )}
                                                                    >
                                                                        {linkifyText(msg.text, { onLinkClick: handleLinkClick })}
                                                                        {!!msg.editedAt && (
                                                                            <span className="ml-2 text-xs text-muted-foreground">(edited)</span>
                                                                        )}
                                                                    </p>
                                                                )}
                                        
                                                                {!!msg.attachments?.length && (() => {
                                                                    const media = msg.attachments.filter((a: any) =>
                                                                        a.type?.startsWith("image/") || a.type?.startsWith("video/")
                                                                    );
                                                                    const audio = msg.attachments.filter((a: any) => a.type?.startsWith("audio/"));
                                                                    const file = msg.attachments.filter((a: any) => {
                                                                        const t = a.type ?? "";
                                                                        return !t.startsWith("image/") && !t.startsWith("video/") && !t.startsWith("audio/");
                                                                    });
                                            
                                                                    if (media.length === 0 && audio.length === 0 && file.length === 0) return null;
                                            
                                                                    return (
                                                                        <div className="mt-1 mb-1 space-y-2">
                                                                            {media.length > 0 && (
                                                                                <div
                                                                                    className={cn(
                                                                                        "grid gap-2 justify-items-start",
                                                                                            media.length === 1 ? "grid-cols-1" : "grid-cols-2",
                                                                                            media.length > 1 && "max-w-lg"
                                                                                    )}
                                                                                >
                                                                                    {media.map((a: any, idx: number) => (
                                                                                        <ChatMessageItem
                                                                                            key={a.url ?? `media-${idx}`}
                                                                                            channelId={Number(channelId)}
                                                                                            assetUrl={a.url ?? ""}
                                                                                            alt={a.name ?? (a.type?.startsWith("video/") ? "video" : "image")}
                                                                                            kind={a.type?.startsWith("video/") ? "video" : "image"}
                                                                                            variant={media.length === 1 ? "single" : "tile"}
                                                                                            onMediaClick={onMediaClick}
                                                                                            isUploading={msg.sending ?? false}
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                
                                                                            {audio.length > 0 && (
                                                                                <div className="space-y-2">
                                                                                    {audio.map((a: any, idx: number) => (
                                                                                        <ChatAudioItem
                                                                                            key={a.url ?? `audio-${idx}`}
                                                                                            channelId={Number(channelId)}
                                                                                            assetUrl={a.url ?? ""}
                                                                                            name={a.name ?? "audio"}
                                                                                            isUploading={msg.sending ?? false}
                                                                                            thread
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                
                                                                            {file.length > 0 && (
                                                                                <div className="space-y-2">
                                                                                    {file.map((a: any, idx: number) => (
                                                                                        <ChatFileItem
                                                                                            key={a.url ?? `file-${idx}`}
                                                                                            channelId={Number(channelId)}
                                                                                            assetUrl={a.url ?? ""}
                                                                                            name={a.name}
                                                                                            isUploading={msg.sending ?? false}
                                                                                            thread
                                                                                        />
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    {(isRoot) && (
                                                        <div className="mt-4 mb-4 px-4 flex items-center gap-3">
                                                            {replyCount > 0 &&
                                                                <div className="text-sm text-muted-foreground">
                                                                    {replyCount} {replyCount === 1 ? "reply" : "replies"}
                                                                </div>
                                                            }
                                                            <div className="h-px flex-1 bg-border" />
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-sm text-muted-foreground">Message not found.</div>
                    )}
                </div>
            </div>

            <div className="border-t border-border bg-background p-4">
                <div className="mx-auto max-w-4xl">
                    {pendingThreadAttachments.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {pendingThreadAttachments.map((a) => (
                                <div
                                    key={a.id}
                                    className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-2"
                                >
                                    {a.file.type.startsWith("image/") ? (
                                        <img
                                            src={a.previewUrl}
                                            alt={a.file.name}
                                            className="h-10 w-10 rounded object-cover"
                                        />
                                    ) : a.file.type.startsWith("video/") ? (
                                        <video
                                            src={a.previewUrl}
                                            className="h-10 w-10 rounded object-cover"
                                            muted
                                            playsInline
                                            preload="metadata"
                                        />
                                    ) : a.file.type.startsWith("audio/") ? (
                                        <Volume2 className="h-10 w-10 text-muted-foreground" />
                                    ) : (
                                        <FileIcon className="h-10 w-10 text-muted-foreground" />
                                    )}

                                    {!a.file.type.startsWith("image/") && !a.file.type.startsWith("video/") && (
                                        <div className="min-w-0">
                                            <div className="max-w-[100px] truncate text-sm">{a.file.name}</div>
                                        </div>
                                    )}

                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => {
                                            setPendingThreadAttachments((prev) => {
                                                const found = prev.find((x) => x.id === a.id);
                                                if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
                                                return prev.filter((x) => x.id !== a.id);
                                            });
                                        }}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                                threadFileInputRef.current?.click()
                            }}
                        >
                            <Plus className="h-5 w-5" />
                        </Button>

                        <input
                            ref={threadFileInputRef}
                            type="file"
                            hidden
                            multiple
                            onChange={(e) => {
                                const input = e.currentTarget;
                                const files = Array.from(input.files ?? []);
                                if (!files.length) return;

                                setPendingThreadAttachments(prev =>
                                    prev.concat(
                                        files.map(file => {
                                            const mime = file.type || mimeFromExt(file.name) || "application/octet-stream";
                                            const blobUrl = URL.createObjectURL(file);
                        
                                            return {
                                                id: crypto.randomUUID(),
                                                file,
                                                type: mime,  
                                                previewUrl: blobUrl
                                            }
                                        })
                                    )
                                );

                                input.value = "";
                            }}
                        />
                        <div
                            className={cn(
                                "flex-1 rounded-md transition",
                                isDraggingThreadFile && "ring-2 ring-primary ring-inset bg-primary/5"
                            )}
                            onDragEnter={() => setIsDraggingThreadFile(true)}
                            onDragLeave={() => setIsDraggingThreadFile(false)}
                            onDrop={(e) => {
                                handleDropInput({
                                    e,
                                    setDraggingFile: setIsDraggingThreadFile,
                                    setPendingAttachments: setPendingThreadAttachments
                                })
                            }}
                            onPaste={(e) => {
                                handlePasteAttachments({
                                    e, 
                                    setDraggingFile: setIsDraggingThreadFile, 
                                    setPendingAttachments: setPendingThreadAttachments
                                })
                            }}
                        >
                            <Textarea
                                placeholder={`Reply`}
                                className="w-full max-h-40 overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words"
                                value={threadMessageText}
                                onChange={(e) => setThreadMessageText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend(activeThreadId ?? undefined);
                                    }
                                }}
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                        >
                            <Smile className="h-5 w-5" />
                        </Button>
                        <Button 
                            className="flex-shrink-0" 
                            onClick={() => handleSend(activeThreadId ?? undefined)}
                            disabled={
                                createMessageMutation.isPending || 
                                (!threadMessageText.trim() && pendingThreadAttachments.length === 0)
                            }
                        >
                            <Send className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
	)
}

function parseAssetsUrl(src: string): { bucket: string; key: string } | null {
	if (!src?.startsWith("assets://")) return null;

	const withoutScheme = src.slice("assets://".length);

	const [bucket, ...rest] = withoutScheme.split("/");
	const key = rest.join("/");

	if (!bucket || !key) return null;
	return { bucket, key };
}

export function ChatMessageItem({
	channelId,
	assetUrl,
	alt,
	variant,
	kind,
	onMediaClick,
	isUploading,
}: {
	channelId: number;
	assetUrl: string;
	alt: string;
	variant?: "single" | "tile";
	kind: "image" | "video";
	onMediaClick?: (src: string, alt: string, kind: "image" | "video") => void;
	isUploading?: boolean;
}) {
	const isLocalPreview = assetUrl.startsWith("blob:");
	const parsed = !isLocalPreview ? parseAssetsUrl(assetUrl) : null;

	const { data, isLoading } = useQuery({
		...trpc.message.getPresignedFileDownloadUrl.queryOptions(
			parsed ? { channelId, key: parsed.key } : { channelId, key: "" }
		),
		enabled: !!parsed,
	});


	const src = parsed ? data?.url : assetUrl;
	const isTile = variant === "tile";

	const [readySrc, setReadySrc] = useState<string | null>(null);
	const isReady = !!src && readySrc === src;

	const showLoadingPlaceholder = !isLocalPreview && !isUploading && (isLoading || !src || !isReady);

	return (
		<div
			onClick={(e) => {
				if (showLoadingPlaceholder) e.preventDefault();
			}}
			className={cn(
				"relative overflow-hidden rounded-lg cursor-pointer",
				// This ensures there is ALWAYS a box size immediately:
				isTile ? "w-full aspect-square" : "inline-block max-w-full"
			)}
		>
			<div className={cn(isTile ? "h-full w-full" : "h-64 w-full")}>
				{showLoadingPlaceholder && (
					<div className={cn(isTile ? 
						"absolute inset-0 z-10 flex items-center justify-center bg-muted/30" : 
						"flex h-64 w-[12rem] max-w-full items-center justify-center bg-muted/30"
					)}>
						<div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
					</div>
				)}

				{isUploading && (
					<div className="absolute inset-0 z-20 flex items-center justify-center bg-background/30 backdrop-blur-[2px]">
						<div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
					</div>
				)}
		
				{src && kind === "image" && (
					<img
						src={src}
						alt={alt}
						className={cn(
							"h-full w-full",
							isTile ? "object-cover" : "object-contain",
							// hide until ready (prevents black flash)
							isLocalPreview ? "opacity-100" : isReady ? "opacity-100" : "opacity-0",
							"transition-opacity",
							showLoadingPlaceholder && "grayscale-[0.2]"
						)}
						onClick={(e) => {
							if (!showLoadingPlaceholder && src && isReady) {
								e.stopPropagation();
								onMediaClick?.(src, alt, "image");
							}
						}}
						onLoad={(e) => {
							const img = e.currentTarget;
						  
							if (img.decode) {
								img.decode().catch(() => {}).finally(() => {
									setReadySrc(src);
								});
							} else {
								setReadySrc(src);
							}
						}}
						onError={() => {
							setReadySrc(src)
						}}
					/>
				)}

				{src && kind === "video" && (
					<div className={cn("relative h-full w-full cursor-pointer", isTile && "group")}>
						<video
							src={src}
							className={cn(
								"h-full w-full",
								isTile ? "object-cover" : "object-contain",
								// same “hide until ready” idea:
								isLocalPreview ? "opacity-100" : isReady ? "opacity-100" : "opacity-0",
								"transition-opacity"
							)}
							onClick={(e) => {
								if (!showLoadingPlaceholder && src && isReady) {
									e.stopPropagation();
									onMediaClick?.(src, alt, "video");
								}
							}}
							preload="metadata"
							onCanPlay={() => setReadySrc(src)}
							onError={() => setReadySrc(src)}
						/>
						{src && isReady && !isUploading && (
							<button
								type="button"
								aria-label="Play video"
								className={cn(
									"absolute inset-0 flex items-center justify-center focus:outline-none focus-visible:outline-none",
								)}
								onClick={(e) => {
									e.stopPropagation();
									onMediaClick?.(src, alt, "video");
								}}
							>
								<span
									className={cn(
										"flex h-14 w-14 items-center justify-center rounded-full",
										"bg-black/55 text-white backdrop-blur-sm",
										"transition-transform",
									)}
								>
									<Play className="h-7 w-7" />
								</span>
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export function ChatAudioItem({
	channelId,
	assetUrl,
	name,
	isUploading,
	thread,
}: {
	channelId: number;
	assetUrl: string;
	name: string;
	isUploading?: boolean;
	thread?: boolean;
}) {
	const safeUrl = assetUrl ?? "";
	const isLocalPreview = safeUrl.startsWith("blob:");
	const parsed = !isLocalPreview ? parseAssetsUrl(safeUrl) : null;

	const { data, isLoading } = useQuery({
		...trpc.message.getPresignedFileDownloadUrl.queryOptions(
			parsed ? { channelId, key: parsed.key } : { channelId, key: "" }
		),
		enabled: !!parsed,
	});

	const src = parsed ? data?.url : safeUrl;
	const showLoadingPlaceholder = !isLocalPreview && !isUploading && (isLoading || !src);

	return (
		<div className="max-w-96 rounded-md border border-border bg-card p-3 mr-2">
			<div className="flex items-center justify-between gap-3">
				<div className="flex min-w-0 items-center gap-2">
					<Volume2 className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
					<div className="min-w-0 text-sm">{name}</div>
				</div>
		
				{isUploading && (
					<div className="flex items-center gap-2 text-xs text-muted-foreground">
						<div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
						<span>Uploading…</span>
					</div>
				)}
			</div>
	  
			<div className="mt-2">
				{showLoadingPlaceholder ? (
					<div className="flex items-center gap-2">
						<div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
						<span className="text-xs text-muted-foreground">Loading…</span>
					</div>
				) : src ? (
					<audio
						className="block w-full h-8"
						controls
						preload="metadata"
						src={src}
					/>
				) : (
					<div className="text-xs text-muted-foreground">Uploading…</div>
				)}
			</div>
		</div>
	);
}

export function ChatFileItem({
	channelId,
	assetUrl,
	name,
	isUploading,
	thread,
}: {
	channelId: number;
	assetUrl: string;
	name: string;
	isUploading?: boolean;
	thread?: boolean;
}) {
	const safeUrl = assetUrl ?? "";
	const isLocalPreview = safeUrl.startsWith("blob:");
	const parsed = !isLocalPreview ? parseAssetsUrl(safeUrl) : null;

	const { data, isLoading } = useQuery({
		...trpc.message.getPresignedFileDownloadUrl.queryOptions(
			parsed ? { channelId, key: parsed.key } : { channelId, key: "" }
		),
		enabled: !!parsed,
	});

	const src = parsed ? data?.url : safeUrl;
	const showLoadingPlaceholder = !isLocalPreview && !isUploading && (isLoading || !src);

	return (
		<a
			href={src || undefined}
			target="_blank"
			rel="noreferrer"
			className="block max-w-96 min-w-0 rounded-md border border-border bg-card p-3 mr-2"
		>
			<div className="flex min-w-0 items-center gap-3">
				<div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted/40">
					<FileIcon className="h-6 w-6 text-muted-foreground" />
				</div>

				<div className="flex-1 min-w-0">
					<div
						className={cn(
							"text-sm underline-offset-2 hover:underline",
							(showLoadingPlaceholder || isUploading)
								? "italic text-muted-foreground"
								: "text-primary"
						)}
					>
						{showLoadingPlaceholder
							? "loading..."
							: isUploading
							? "uploading..."
							: name}
					</div>
				</div>
			</div>
		</a>
	);
}
